import { t } from 'i18next';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SelectInstance } from 'react-select';
import { TTeamSummary } from '../../types/teamTypes';
import { TClassSummary, TRealmResource, TRegionSummary } from '../../types/utilityTypes';
import { commonApiServiceAsync } from '../scripts/services/commonApiService';
import { ClassIcon } from '../scripts/wow/components/ClassIcon';
import { IRioSelectProps } from './presentational/select/types';
import { RioSelect } from './presentational/select/Select';
import { InlineSpinner } from './presentational/spinner';
import { Stack } from './stack/Stack';
import { TSharedGuildSummary } from '../../types/guildTypes';

export type TRealmSuggestionOption = {
  type: 'realm';
  data: {
    realm: TRealmResource;
    region: TRegionSummary;
  };
  name?: string;
};

export type TGuildSuggestionOption = {
  type: 'guild';
  data: TSharedGuildSummary;
  name?: string;
};

export type TTeamSuggestionOption = {
  type: 'team' | 'active_team';
  data: TTeamSummary & {
    keystone_platoon_id: number | null | undefined;
    faction: string;
  };
  name?: string;
};

export type TCharacterSuggestionOption = {
  type: 'character';
  data: {
    id: number;
    name: string;
    faction: string;
    region: TRegionSummary;
    realm: TRealmResource;
    class: TClassSummary;
    classId: number;
  };
  name?: string;
};

export type TCommonSuggestionOption = {
  type: 'boss' | 'custom';
  data: any;
  name?: string;
};

export type TSuggestionOption =
  | TRealmSuggestionOption
  | TGuildSuggestionOption
  | TTeamSuggestionOption
  | TCommonSuggestionOption
  | TCharacterSuggestionOption;

type EntityType = 'all' | 'realm' | 'guild' | 'team' | 'active_team' | 'character';

export interface IEntityAsyncSelectProps<Value = any, IsClearable extends boolean = false>
  extends Omit<IRioSelectProps<TSuggestionOption, Value, false, IsClearable>, 'loadOptions'> {
  type?: EntityType;
  showThatRealmsAreConnected?: boolean;
  getDefaultOnChange?: {
    searchTerm?: string;
    filter: (option: TSuggestionOption) => boolean;
  };
  fetchParams?: {
    region?: string;
  };
  loadValueOnMount?: boolean;
}

const renderSuggestion = <Value, IsClearable extends boolean>(
  option: TSuggestionOption,
  props: IEntityAsyncSelectProps<Value, IsClearable>
) => {
  switch (option.type) {
    case 'guild': {
      return (
        <span className="slds-truncate">
          <span className="text-muted rio-text-body--x-small">{t('quicksearch:types.guild')}</span>
          &nbsp; <span className={`rio-faction-color--${option.data.faction}`}>{option.data.displayName}</span>
          &nbsp;{' '}
          <span className="rio-text-body--small">
            {option.data.region.short_name}-{option.data.realm.name}
          </span>
        </span>
      );
    }
    case 'team':
    case 'active_team': {
      return (
        <span className="slds-truncate">
          <span className="text-muted rio-text-body--x-small">{t('quicksearch:types.team')}</span>
          &nbsp; <span className={`rio-faction-color--${option.data.faction}`}>{option.data.name}</span>
          &nbsp; <span className="rio-text-body--small">{option.data.region.short_name}</span>
        </span>
      );
    }
    case 'character': {
      return (
        <span className="slds-truncate">
          <span className="text-muted rio-text-body--x-small">{t('quicksearch:types.character')}</span>
          &nbsp; &nbsp;
          <span className={`rio-faction-color--${option.data.faction} class-color--${option.data.classId}`}>
            <span className="slds-avatar slds-avatar--x-small slds-m-right--x-small">
              <ClassIcon class={option.data.class.slug} />
            </span>
            {option.data.name}
          </span>
          &nbsp;{' '}
          <span className="rio-text-body--small">
            {option.data.region.short_name}-{option.data.realm.name}
          </span>
        </span>
      );
    }
    case 'realm': {
      const connectedText = props.showThatRealmsAreConnected ? (
        <span style={{ opacity: 0.5 }}>{t('quicksearch:connected-realm-indicator')}</span>
      ) : (
        ''
      );

      const getRealmName = () => {
        if (option.data.realm.altName) {
          return `${option.data.realm.altName} (${option.data.realm.name})`;
        }

        return option.data.realm.name;
      };

      return (
        <span>
          <span className="text-muted rio-text-body--x-small">{t('quicksearch:types.realm')}</span>
          &nbsp;{option.data.region.short_name}-{getRealmName()} {connectedText}
        </span>
      );
    }
    case 'boss': {
      return (
        <span>
          <span className="text-muted rio-text-body--x-small">{t('quicksearch:types.boss')}</span>
          &nbsp; <b>{option.data.boss.short_name}</b>
          &nbsp; <span className="rio-text-body--x-small">({option.data.raid.short_name})</span>
        </span>
      );
    }
    default: {
      return <span>UNKNOWN</span>;
    }
  }
};

export const EntityAsyncSelect = <Value, IsClearable extends boolean = false>({
  type = 'all',
  minSearchLengthToLoadOptions = 3,
  theme = 'light',
  size = 'small',
  fetchParams,
  prefix = <i className="fa fa-search" />,
  placeholder = t('quicksearch:placeholder'),
  options = [],
  loadValueOnMount = false,
  getDefaultOnChange,
  ...props
}: IEntityAsyncSelectProps<Value, IsClearable>) => {
  const selectRef = useRef<SelectInstance | null>(null);
  const [baseOptions, setBaseOptions] = useState<TSuggestionOption[]>([]);

  useEffect(() => {
    const fetchBaseOptions = async () => {
      try {
        const response = await commonApiServiceAsync.search({
          type,
          term: props.value
        });

        setBaseOptions(response.matches);
      } catch (error) {
        console.error({ error }, 'error fetching base options');
      }
    };

    if (props.value && !getDefaultOnChange && loadValueOnMount) {
      fetchBaseOptions();
    }

    const getDefaultOption = async () => {
      if (!getDefaultOnChange) {
        return;
      }

      const term = getDefaultOnChange.searchTerm?.trim();

      if (!term || term.length < minSearchLengthToLoadOptions) {
        return;
      }

      try {
        const response = await commonApiServiceAsync.search({
          type,
          term
        });

        const predicate = getDefaultOnChange.filter;

        const option = response.matches.find(predicate);

        if (!option) {
          return;
        }

        const getOptionValue = props.getOptionValue || _.identity;

        props.onChange(getOptionValue(option) as Value, option);
        setBaseOptions((current) => [...current, option]);
      } catch (error) {
        console.error({ error }, 'error fetching default option');
      }
    };

    if (getDefaultOnChange) {
      getDefaultOption();
    }
  }, []);

  const loadOptions = useCallback(
    async (input: string) => {
      try {
        const response = await commonApiServiceAsync.search({
          type,
          term: input,
          ...(fetchParams?.region ? { region: fetchParams.region } : {})
        });

        return response.matches;
      } catch (error) {
        console.error({ error }, 'error loading options');
        return [];
      }
    },
    [type, fetchParams?.region]
  );

  return (
    <RioSelect<TSuggestionOption, Value, false, IsClearable>
      selectRef={selectRef}
      options={[...baseOptions, ...options]}
      loadOptions={loadOptions}
      optionsLoadingMessage={() => (
        <Stack alignItems="center">
          <span className="rio-text-body--small">{t('quicksearch:searching')} &nbsp; </span>
          <InlineSpinner inline className="rio-spinner--inline-small" style={{ margin: 'initial' }} />
        </Stack>
      )}
      formatOptionLabel={(option) => renderSuggestion(option, props)}
      {...props}
      minSearchLengthToLoadOptions={minSearchLengthToLoadOptions}
      theme={theme}
      size={size}
      prefix={prefix}
      placeholder={placeholder}
    />
  );
};
